import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';

import type { PermissionKey } from '../api/schemas';
import { LoadingState } from '../components/LoadingState';
import { isCardLockedNow } from '../lib/trial';
import { insideNativeApp } from './native-app';
import { isCoach, isOwner } from './roles';
import { usePermissions } from './usePermissions';
import { useSession } from './useSession';

type RequireAuthProps = {
  children: ReactNode;
  /** Owner-only routes redirect an employee back to / with a notice. */
  ownerOnly?: boolean;
  /** The card step itself (/pago): reachable while a new signup waits for Stripe to confirm its card. */
  allowCardLocked?: boolean;
  /** The door the owner can open or close per profile (Equipo, Qué ve cada perfil). */
  permission?: PermissionKey;
  /** A coach keeps their own view of this place even without the permission (their calendar). */
  allowCoach?: boolean;
  /** The plan and the card (Membresía, /pago): inside Dale Negocios they send home, since Apple allows no way to pay there. */
  webOnly?: boolean;
};

const CARD_LOCKED_TEXT = 'Tu negocio está terminando de activarse. Pídele a la persona dueña que agregue la tarjeta en Dale.';
/** The owner inside the app: the same pause, with no way to pay pointed at (App Store 3.1.3(f)). */
const CARD_LOCKED_IN_APP_TEXT = 'Tu negocio está terminando de activarse.';

export function RequireAuth({
  children,
  ownerOnly = false,
  allowCardLocked = false,
  permission,
  allowCoach = false,
  webOnly = false,
}: RequireAuthProps) {
  const { status, session } = useSession();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const mustChangePassword = status === 'authenticated' && Boolean(session?.mustChangePassword);
  // A new signup whose trial is over and whose card Stripe has not confirmed:
  // the whole panel waits on the card step, the same way the API answers 402
  // card_required. A trial that ends mid-session locks on the next page.
  const cardLocked = status === 'authenticated' && !mustChangePassword && isCardLockedNow(session) && !allowCardLocked;
  const inApp = insideNativeApp(session);
  const sentHome = status === 'authenticated' && !mustChangePassword && webOnly && inApp;
  const ownerCardLocked = cardLocked && isOwner(session?.role) && !inApp && !sentHome;
  const settled = status === 'authenticated' && !mustChangePassword && !cardLocked;
  const lacksPermission =
    permission !== undefined && permissions.ready && !permissions.can(permission) && !(allowCoach && isCoach(session));
  const forbidden = settled && ((ownerOnly && !isOwner(session?.role)) || lacksPermission);
  const waitingOnPermissions = settled && permission !== undefined && !permissions.ready;

  useEffect(() => {
    if (status === 'unauthenticated') {
      void navigate({ to: '/entrar', replace: true });
    }
  }, [status, navigate]);

  // The forced password change blocks the whole panel until it resolves
  // (docs/FLOWS.md §2): every route behind RequireAuth bounces to
  // /cambiar-contrasena while the flag is set, regardless of ownerOnly.
  useEffect(() => {
    if (mustChangePassword) {
      void navigate({ to: '/cambiar-contrasena', replace: true });
    }
  }, [mustChangePassword, navigate]);

  useEffect(() => {
    if (sentHome) {
      void navigate({ to: '/', replace: true });
    }
  }, [sentHome, navigate]);

  useEffect(() => {
    if (ownerCardLocked) {
      void navigate({ to: '/pago', replace: true, search: { desde: 'registro' } });
    }
  }, [ownerCardLocked, navigate]);

  useEffect(() => {
    if (forbidden) {
      void navigate({ to: '/', replace: true, search: { notice: lacksPermission ? 'sin-permiso' : 'solo-dueno' } });
    }
  }, [forbidden, lacksPermission, navigate]);

  if (status === 'loading') return <LoadingState label="Cargando tu sesión…" />;
  if (status !== 'authenticated') return null;
  if (mustChangePassword) return null;
  if (sentHome) return null;
  if (ownerCardLocked) return null;
  if (cardLocked) {
    return (
      <main className="card-locked" role="status">
        <p>{inApp && isOwner(session?.role) ? CARD_LOCKED_IN_APP_TEXT : CARD_LOCKED_TEXT}</p>
      </main>
    );
  }
  if (waitingOnPermissions) return <LoadingState label="Cargando tus permisos…" />;
  if (forbidden) return null;

  return <>{children}</>;
}

type RequireMustChangePasswordProps = {
  children: ReactNode;
};

/**
 * Guards `/cambiar-contrasena` itself: reachable only while
 * `session.mustChangePassword` is true. An unauthenticated visitor goes to
 * /entrar; an authenticated one with nothing to change goes to / — there is
 * no other way into or out of this screen (no nav chrome renders around it).
 */
export function RequireMustChangePassword({ children }: RequireMustChangePasswordProps) {
  const { status, session } = useSession();
  const navigate = useNavigate();
  const allowed = status === 'authenticated' && Boolean(session?.mustChangePassword);

  useEffect(() => {
    if (status === 'unauthenticated') {
      void navigate({ to: '/entrar', replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (status === 'authenticated' && !allowed) {
      void navigate({ to: '/', replace: true });
    }
  }, [status, allowed, navigate]);

  if (status === 'loading') return <LoadingState label="Cargando tu sesión…" />;
  if (status !== 'authenticated') return null;
  if (!allowed) return null;

  return <>{children}</>;
}
